'use client';
import { useState } from 'react';
import type { BattingStats, BowlingStats } from '@/lib/tourData';

interface StatsTabProps {
  battingStats: BattingStats[];
  bowlingStats: BowlingStats[];
}

export default function StatsTab({ battingStats, bowlingStats }: StatsTabProps) {
  const [statsType, setStatsType] = useState<'batting' | 'bowling'>('batting');

  return (
    <div>
      {/* Stats type toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setStatsType('batting')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
            statsType === 'batting' ?'bg-rc-orange text-white' :'bg-rc-card border border-rc-border text-white/60 hover:text-white'
          }`}
        >
          Batting
        </button>
        <button
          onClick={() => setStatsType('bowling')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
            statsType === 'bowling' ?'bg-rc-orange text-white' :'bg-rc-card border border-rc-border text-white/60 hover:text-white'
          }`}
        >
          Bowling
        </button>
      </div>

      {/* Batting Stats */}
      {statsType === 'batting' && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'rgb(18, 23, 33)' }}>
                <th className="text-left px-4 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Player</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">M</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Inn</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Runs</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">HS</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Avg</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">SR</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">100s</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">50s</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">4s</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">6s</th>
              </tr>
            </thead>
            <tbody>
              {battingStats?.map((stat, index) => (
                <tr
                  key={stat.player}
                  className="border-t border-white/[0.06] hover:bg-white/[0.03] transition-colors"
                >
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-white/30 text-xs w-4">{index + 1}</span>
                      <div>
                        <p className="text-white/85 text-sm font-medium">{stat.player}</p>
                        <p className="text-white/40 text-xs">{stat.team}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.matches}</td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.innings}</td>
                  <td className="px-3 py-3.5 text-center">
                    <span className="text-white font-bold text-sm">{stat.runs}</span>
                  </td>
                  <td className="px-3 py-3.5 text-center text-white/70 text-sm">{stat.highScore}</td>
                  <td className="px-3 py-3.5 text-center text-white/70 text-sm">{stat.average.toFixed(2)}</td>
                  <td className="px-3 py-3.5 text-center">
                    <span className={`text-sm font-medium ${
                      stat.strikeRate >= 180 ? 'text-rc-green' :
                      stat.strikeRate >= 150 ? 'text-rc-orange': 'text-white/70'
                    }`}>
                      {stat.strikeRate.toFixed(1)}
                    </span>
                  </td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.centuries}</td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.halfCenturies}</td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.fours}</td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.sixes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bowling Stats */}
      {statsType === 'bowling' && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'rgb(18, 23, 33)' }}>
                <th className="text-left px-4 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Player</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">M</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Inn</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Wkts</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Best</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Avg</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Econ</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">SR</th>
                <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">5W</th>
              </tr>
            </thead>
            <tbody>
              {bowlingStats?.map((stat, index) => (
                <tr
                  key={stat.player}
                  className="border-t border-white/[0.06] hover:bg-white/[0.03] transition-colors"
                >
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-white/30 text-xs w-4">{index + 1}</span>
                      <div>
                        <p className="text-white/85 text-sm font-medium">{stat.player}</p>
                        <p className="text-white/40 text-xs">{stat.team}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.matches}</td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.innings}</td>
                  <td className="px-3 py-3.5 text-center">
                    <span className="text-white font-bold text-sm">{stat.wickets}</span>
                  </td>
                  <td className="px-3 py-3.5 text-center text-white/70 text-sm">{stat.bestFigures}</td>
                  <td className="px-3 py-3.5 text-center text-white/70 text-sm">{stat.average.toFixed(2)}</td>
                  <td className="px-3 py-3.5 text-center">
                    <span className={`text-sm font-medium ${
                      stat.economy <= 7.0 ? 'text-rc-green' :
                      stat.economy <= 8.0 ? 'text-rc-orange': 'text-red-400'
                    }`}>
                      {stat.economy.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-3 py-3.5 text-center text-white/70 text-sm">{stat.strikeRate.toFixed(1)}</td>
                  <td className="px-3 py-3.5 text-center text-white/60 text-sm">{stat.fiveWickets}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
